'use client';

import { Box, X } from 'lucide-react';
import { useFormatter, useTranslations } from 'next-intl';
import { type FC, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ElementCard } from '@/app/[locale]/(app)/(workspace)/(main)/assets/myreferences/_components/ElementCard';
import { BaseLineRecreate } from '@/app/components/icons/BaseLineRecreate';
import { Fail } from '@/app/components/icons/Fail';
import { Loading } from '@/app/components/icons/Loading';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { Button } from '@/app/components/ui/button';
import { useStore } from '@/app/providers/store';
import type { ElementType, ViduV1Element } from '@/app/types';
import { cn, convertViduV1ElementToElementType } from '@/app/utils';
import type { CharacterElementTaskHistory, CharacterElementTaskHistoryItem } from '../types';
import { CharacterContinueCreateButton } from './CharacterContinueCreateButton';

interface CharacterElementListProps {
  tasks: CharacterElementTaskHistory[];
  loading?: boolean;
  loadingMore?: boolean;
  noMore?: boolean;
  onRefresh: () => void;
  onLoadMore: () => void;
  onCancelTask?: (taskId: string) => void;
  onReEditTask?: (task: CharacterElementTaskHistory) => void;
}

const SCROLLABLE_TARGET_ID = 'character-elements-scrollable-target';

const isQueueingState = (state?: CharacterElementTaskHistory['state'] | CharacterElementTaskHistoryItem['state']) =>
  state === 'created' || state === 'queueing' || state === 'preparation' || state === 'scheduling';

const isRunningState = (state?: CharacterElementTaskHistory['state'] | CharacterElementTaskHistoryItem['state']) =>
  isQueueingState(state) || state === 'processing' || !state || state === 'unspecified';

const isFailedState = (state?: CharacterElementTaskHistory['state'] | CharacterElementTaskHistoryItem['state']) =>
  state === 'failed';

const isCanceledState = (state?: CharacterElementTaskHistory['state'] | CharacterElementTaskHistoryItem['state']) =>
  state === 'canceled';

const getTaskStateLabel = (
  t: ReturnType<typeof useTranslations>,
  state?: CharacterElementTaskHistory['state'] | CharacterElementTaskHistoryItem['state'],
  fallback?: string,
) => {
  if (isFailedState(state)) return fallback || t('tools.characterCreation.error.failed');
  if (isCanceledState(state)) return t('task.status.canceled.content');
  if (isQueueingState(state)) return t('task.status.queue.title');
  return t('tools.characterCreation.elementList.processing');
};

const convertMaterialElementToViduElement = (
  element: NonNullable<CharacterElementTaskHistoryItem['element']>,
): ViduV1Element => ({
  ...element,
  share_meta: element.share
    ? {
        id: element.share.id,
        status: element.share.status,
      }
    : undefined,
});

export const CharacterElementList: FC<CharacterElementListProps> = ({
  tasks,
  loading,
  loadingMore,
  noMore,
  onRefresh,
  onLoadMore,
  onCancelTask,
  onReEditTask,
}) => {
  const t = useTranslations();
  const format = useFormatter();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [cancelingTaskId, setCancelingTaskId] = useState<string>();
  const { openReferenceModalWithData, isTeamSpace } = useStore((state) => ({
    openReferenceModalWithData: state.openReferenceModalWithData,
    isTeamSpace: state.isTeamSpace,
  }));

  const handleEdit = (element: ElementType) => {
    openReferenceModalWithData({
      referenceModalOpen: true,
      element,
      referenceModalMode: 'edit',
      referenceModalFrom: 'others',
      referenceModalFromPage: 'reference_asset',
    });
  };

  const handleViewDetails = (element: ElementType) => {
    openReferenceModalWithData({
      referenceModalOpen: true,
      element,
      referenceModalMode: 'view',
      referenceModalFrom: 'others',
      referenceModalFromPage: 'reference_asset',
    });
  };

  const handlePublish = (element: ElementType) => {
    openReferenceModalWithData({
      referenceModalOpen: true,
      element,
      referenceModalType: 'share',
      referenceModalMode: 'post',
      referenceModalFrom: 'others',
      referenceModalFromPage: 'reference_asset',
    });
  };

  const handleDelete = () => {
    onRefresh();
  };

  const handleCancelTask = (taskId?: string) => {
    if (!taskId || cancelingTaskId) return;
    setCancelingTaskId(taskId);
    onCancelTask?.(taskId);
    window.setTimeout(() => setCancelingTaskId(undefined), 1200);
  };

  const renderPlaceholderItem = (task: CharacterElementTaskHistory, index: number) => {
    const failed = isFailedState(task.state);
    const canceled = isCanceledState(task.state);

    return (
      <div
        key={`${task.id || 'task'}-${index}`}
        className={cn(
          'flex h-[120px] w-full flex-col items-center justify-center gap-2 rounded-16 bg-system-black px-5 py-5 text-center text-sm text-system-white64 lg:gap-1 lg:px-4 lg:text-xs',
          failed && 'text-system-white64',
        )}
      >
        <span className="shrink-0 text-2xl lg:text-xl">
          {failed || canceled ? <Fail /> : <Loading className="animate-spin text-system-white" />}
        </span>
        <p className="font-medium text-lg lg:text-base">{getTaskStateLabel(t, task.state)}</p>
      </div>
    );
  };

  const renderTaskItem = (
    task: CharacterElementTaskHistory,
    item: CharacterElementTaskHistoryItem,
    index: number,
    showItemContinueCreate: boolean,
  ) => {
    if (item.element) {
      const element = convertMaterialElementToViduElement(item.element);
      return (
        <ElementCard
          key={item.element.id || item.element_id || `${task.id}-${index}`}
          sourceElement={element}
          isTeamSpace={isTeamSpace}
          onEdit={() => handleEdit(convertViduV1ElementToElementType(element))}
          onViewDetails={handleViewDetails}
          onDelete={handleDelete}
          handlePublish={handlePublish}
          actionBeforePublish={
            showItemContinueCreate ? (
              <CharacterContinueCreateButton element={element} prompt={task.prompt} />
            ) : undefined
          }
          className="h-[120px]"
        />
      );
    }

    const failed = isFailedState(item.state);
    const canceled = isCanceledState(item.state);
    return (
      <div
        key={`${task.id || 'task'}-${item.index ?? index}`}
        className={cn(
          'flex h-[120px] w-full flex-col items-center justify-center gap-2 rounded-16 bg-system-black px-5 py-5 text-center text-sm text-system-white64 lg:gap-1 lg:px-4 lg:text-xs',
          failed && 'text-system-white64',
        )}
      >
        <span className="shrink-0 text-2xl lg:text-xl">
          {failed || canceled ? <Fail /> : <Loading className="animate-spin text-system-white" />}
        </span>
        <p className="font-medium text-lg lg:text-base">{getTaskStateLabel(t, item.state, item.err_msg)}</p>
      </div>
    );
  };

  const renderTask = (task: CharacterElementTaskHistory) => {
    const itemCount = Math.max(task.sample_count || 1, task.items?.length || 0);
    const items = Array.from({ length: itemCount }, (_, index) =>
      task.items?.find((item: CharacterElementTaskHistoryItem) => item.index === index),
    );
    const createdAt = task.created_at ? new Date(task.created_at) : null;
    const canCancel = task.state === 'queueing';
    const singleElement =
      itemCount === 1 && items[0]?.element ? convertMaterialElementToViduElement(items[0].element) : null;

    return (
      <section key={task.id} className="rounded-16 px-0 py-0">
        <div className="flex items-center pr-9">
          <div className="size-7 rounded-full bg-system-white10 p-1">
            <Box className="size-5 text-system-white/72" />
          </div>
          <p className="ml-3 text-sm text-system-white opacity-90">{t('tools.characterCreation.title')}</p>
          {createdAt && (
            <div className="ml-2 text-sm text-system-white36">
              {format.dateTime(createdAt, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                timeZone,
              })}
            </div>
          )}
          {canCancel && task.id && (
            <button
              type="button"
              disabled={cancelingTaskId === task.id}
              onClick={() => handleCancelTask(task.id)}
              className={cn(
                'ml-auto flex h-8 shrink-0 items-center gap-1 rounded-8 px-2 text-system-white52 text-xs transition-colors hover:bg-system-white08 hover:text-system-white disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {cancelingTaskId === task.id ? <Loading className="size-3 animate-spin" /> : <X className="size-3" />}
              {t('task.action.cancel')}
            </button>
          )}
        </div>

        {task.prompt && (
          <div className="mt-3 mb-4 ml-10 pr-3 text-sm text-system-white48">
            <p className="line-clamp-2 leading-[1.45]">{task.prompt}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 pl-10 md:grid-cols-1 xl:grid-cols-2">
          {items.map((item, index) => {
            if (item) return renderTaskItem(task, item, index, itemCount > 1);
            if (isRunningState(task.state) || isFailedState(task.state)) return renderPlaceholderItem(task, index);
            return null;
          })}
        </div>

        {(singleElement || onReEditTask) && (
          <div className="mt-3 flex items-center justify-between pl-10 md:pl-0 lg:mt-2">
            <div className="flex items-center gap-2">
              {singleElement && (
                <CharacterContinueCreateButton
                  element={singleElement}
                  prompt={task.prompt}
                  showTriggerLabel
                  triggerClassName="h-8 gap-1 rounded-8 bg-system-bg02 px-3 font-normal enabled:hover:bg-system-hover01"
                />
              )}
              {onReEditTask && (
                <Button
                  className="h-8 gap-2 rounded-8 bg-system-bg02 px-3 font-normal enabled:hover:bg-system-hover01 disabled:text-system-white24"
                  onClick={() => onReEditTask(task)}
                  icon={<BaseLineRecreate className="text-base sm:hidden" />}
                >
                  {t('task.action.recreate')}
                </Button>
              )}
            </div>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <div id={SCROLLABLE_TARGET_ID} className="hide-scrollbar min-h-0 flex-1 overflow-y-auto pr-1">
        {loading && tasks.length === 0 ? (
          <div className="flex h-full min-h-[320px] items-center justify-center">
            <GlobalLoader />
          </div>
        ) : tasks.length === 0 ? (
          <p className="flex h-full min-h-[320px] items-center justify-center text-center light:text-system-black/40 text-sm text-system-white/40">
            {t('tools.characterCreation.elementList.empty')}
          </p>
        ) : (
          <InfiniteScroll
            dataLength={tasks.length}
            next={onLoadMore}
            hasMore={!noMore}
            loader={null}
            scrollThreshold={0.6}
            scrollableTarget={SCROLLABLE_TARGET_ID}
          >
            <div className="flex flex-col gap-8 pb-4">
              {tasks.map(renderTask)}
              {!noMore && loadingMore && <Loading className="mx-auto mt-2 animate-spin" />}
            </div>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};
